import React, { useState } from 'react';
import Column from '@amzn/meridian/column';
import Input from '@amzn/meridian/input';
import Button from '@amzn/meridian/button';
import Alert from '@amzn/meridian/alert';
import Divider from '@amzn/meridian/divider';
import AuthCard from '../components/AuthCard';
import { getAuthService } from '../services';
import { PendingApprovalError } from '../services/types';
import { isSsoEnabled } from '../config/appConfig';
import { useAuth } from '../auth/AuthContext';

interface LoginScreenProps {
    onGoToRegister: () => void;
    onForgotPassword: () => void;
    onMfaRequired: (email: string, password: string) => void;
}

export default function LoginScreen({
    onGoToRegister,
    onForgotPassword,
    onMfaRequired,
}: LoginScreenProps) {
    const { setUser } = useAuth();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async () => {
        setError(null);
        setNotice(null);
        if (!email.trim() || !password) {
            setError('Enter your email and password.');
            return;
        }
        setSubmitting(true);
        try {
            const result = await getAuthService().signIn(email.trim(), password);
            if (result.status === 'EMAIL_MFA_REQUIRED') {
                onMfaRequired(email.trim(), password);
            } else {
                setUser(result.user);
            }
        } catch (e) {
            if (e instanceof PendingApprovalError) {
                setNotice(e.message);
            } else {
                setError(e instanceof Error ? e.message : 'Sign in failed.');
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <AuthCard title="Sign in" subtitle="Access your supplier account">
            <Column spacing="400">
                <Input
                    id="login-email"
                    type="email"
                    label="Email"
                    value={email}
                    autoComplete="email"
                    onChange={setEmail}
                />
                <Input
                    id="login-password"
                    type="password"
                    label="Password"
                    value={password}
                    autoComplete="current-password"
                    onChange={setPassword}
                />
                {notice ? (
                    <Alert type="neutral" size="small">
                        {notice}
                    </Alert>
                ) : null}
                {error ? (
                    <Alert type="error" size="small">
                        {error}
                    </Alert>
                ) : null}
                <Button type="primary" disabled={submitting} onClick={handleSubmit}>
                    {submitting ? 'Signing in…' : 'Sign in'}
                </Button>
                <Button type="link" onClick={onForgotPassword}>
                    Forgot password?
                </Button>
                <Button type="link" onClick={onGoToRegister}>
                    Need an account? Register
                </Button>

                {isSsoEnabled() ? (
                    <>
                        <Divider />
                        <Button
                            type="secondary"
                            onClick={() => {
                                getAuthService()
                                    .signInWithSso()
                                    .catch((e) =>
                                        setError(
                                            e instanceof Error ? e.message : 'SSO sign in failed.',
                                        ),
                                    );
                            }}
                        >
                            Amazon employee? Sign in with Midway
                        </Button>
                    </>
                ) : null}
            </Column>
        </AuthCard>
    );
}
